/**
 * Database Stops Panel
 *
 * Right-hand panel of `StopsTransferSection`: lists stops as they exist on
 * the selected UAT/Production network. Supports refresh, search,
 * select-all, and a "delete selected" action that surfaces to the parent.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Stack, TextField, Typography } from '@mui/material';

import { fetchStopsList, type Environment, type UATStopListItem } from '../../../../services/fixedRouteUatClient';
import { StopCard } from './StopCard';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
interface DatabaseStopsPanelProps {
  jwtToken: string;
  productId: string;
  environment: Environment;
  selectedIds: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  onStopsLoaded?: (stops: UATStopListItem[]) => void;
  disabled?: boolean;
}

export interface DatabaseStopsPanelHandle {
  refresh: () => void;
}

function readCoords(stop: UATStopListItem): { lat: number; lng: number } | undefined {
  const lat = stop.latitude ?? stop.coordinates?.lat;
  const lng = stop.longitude ?? stop.coordinates?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return undefined;
  return { lat, lng };
}

export const DatabaseStopsPanel = forwardRef<DatabaseStopsPanelHandle, DatabaseStopsPanelProps>(
  ({ jwtToken, productId, environment, selectedIds, onSelectionChange, onStopsLoaded, disabled }, ref) => {
    const { t } = useTranslation('mapTransfer');
    const [stops, setStops] = useState<UATStopListItem[]>([]);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
      if (!jwtToken.trim() || !productId) {
        setStops([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchStopsList(jwtToken, productId, 0, 1000, environment);
        const list =
          response.data?.entry || response.data?.entries || response.data?.stops || [];
        setStops(list);
        onStopsLoaded?.(list);
      } catch (e) {
        setError(e instanceof Error ? e.message : t('stops.panel.loadStopsError'));
      } finally {
        setIsLoading(false);
      }
    }, [jwtToken, productId, environment, onStopsLoaded, t]);

    useImperativeHandle(ref, () => ({ refresh: load }), [load]);

    useEffect(() => {
      load();
    }, [load]);

    const visibleStops = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return stops;
      return stops.filter((s) => {
        return (
          (s.name || '').toLowerCase().includes(q) ||
          (s.label || '').toLowerCase().includes(q) ||
          (s.address || '').toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q)
        );
      });
    }, [stops, search]);

    const allSelected = visibleStops.length > 0 && visibleStops.every((s) => selectedIds.has(s.id));
    const someSelected = visibleStops.some((s) => selectedIds.has(s.id));

    const handleToggle = (id: string) => {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange(next);
    };

    const handleSelectAll = () => {
      if (allSelected) {
        const next = new Set(selectedIds);
        visibleStops.forEach((s) => next.delete(s.id));
        onSelectionChange(next);
      } else {
        const next = new Set(selectedIds);
        visibleStops.forEach((s) => next.add(s.id));
        onSelectionChange(next);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {environment}
          </Typography>
          <Chip
            size="small"
            color="info"
            icon={<Icon size={14} name="link" />}
            label={t('stops.panel.inNetworkChip', { count: stops.length })}
          />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={14} /> : <Icon name="arrows-rotate" />}
            onClick={load}
            disabled={disabled || isLoading || !jwtToken.trim() || !productId}
          >
            {t('stops.panel.refresh')}
          </Button>
        </Box>

        <TextField
          size="small"
          placeholder={t('stops.panel.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Icon name="magnifying-glass" size={20} style={{ marginRight: 4 }} /> }}
          sx={{ mb: 1 }}
        />

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              onChange={handleSelectAll}
              disabled={disabled || visibleStops.length === 0}
            />
          }
          label={
            <Typography variant="body2">
              {t('stops.panel.selectAllVisible', { count: visibleStops.length })}
            </Typography>
          }
        />

        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {isLoading && stops.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : visibleStops.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {t('stops.panel.noStops')}
            </Typography>
          ) : (
            visibleStops.map((stop) => (
              <StopCard
                key={stop.id}
                id={stop.id}
                name={stop.name || stop.label || stop.id}
                label={stop.label}
                coordinates={readCoords(stop)}
                address={stop.address}
                selected={selectedIds.has(stop.id)}
                onToggleSelect={handleToggle}
                linkState="remote"
                disabled={disabled}
              />
            ))
          )}
        </Box>
      </Box>
    );
  },
);

DatabaseStopsPanel.displayName = 'DatabaseStopsPanel';

export default DatabaseStopsPanel;
